import * as React from "react";
import { components, type OptionProps, type SingleValueProps } from "react-select";

import { faInfoCircle, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCheck, faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { isSameDay } from "date-fns";
import { compact } from "lodash";
import { commitMutation, fetchQuery, graphql, type RecordProxy, type RecordSourceSelectorProxy } from "relay-runtime";
import { v4 } from "uuid";

import ThemeContext from "~/common/ThemeContext";
import RadioInput from "~/feedback/FormInputs/RadioInput";
import { ReactComponent as GoogleMeetLogo } from "~/legacy-assets/images/logos/google_meet.svg";
import { ReactComponent as GoogleLogo } from "~/legacy-assets/images/logos/gsuite.svg";
import { ReactComponent as MSTeamsLogo } from "~/legacy-assets/images/logos/microsoft-teams-icon.svg";
import { ReactComponent as OutlookLogo } from "~/legacy-assets/images/logos/outlook.svg";
import { ReactComponent as ZoomLogo } from "~/legacy-assets/images/logos/zoom_icon_only.svg";
import growthbook from "~/lib/ab/growthbook";
import { promptGoogleAuth, promptOffice365Auth } from "~/lib/auth";
import { useFeature, useSwitch } from "~/lib/flippy";
import useToggle from "~/lib/hooks/useToggle";
import { currentUserStorageKey, inApplicableEnvironment, openInDesktopStorage } from "~/lib/open-in-desktop-app";
import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { isMacos } from "~/lib/utils";
import WebsocketClient from "~/lib/websocket";
import { environment } from "~/models";
import { LIVE_REACTIONS_FEATURE_FLAG_ID } from "~/stream/LiveReactions";
import { Button, Checkbox, toast, Tooltip } from "~/ui";
import { ButtonLink, IconButton, Input, Switch } from "~/ui/legacyFellowIndex";
import Selector, { type SingleValue } from "~/ui/selectors/Selector";
import { condensedStyles } from "~/ui/selectors/SelectorUtils";
import { ToastActionButton } from "~/ui/toast";

import { DesktopAppSettings, useDesktopSetting } from "../DesktopAppSettings";
import EventFiltersSettings from "../EventFilters";
import {
	Divider,
	InfoBanner,
	Label,
	Left,
	Right,
	Row,
	SectionTitle,
	SettingsPageSubtitle,
	SettingsPageTitle,
	SubLabel,
	useUserSetting,
} from "./shared";
import ThemeSelector from "./ThemeSelector";
import TimezoneSelector, { getTimezoneInfo } from "./TimezoneSelector";

import type { TimezoneInput } from "~/__generated__/ProfileSetImageMutation.graphql";
import type { ConferenceProviderOptions } from "~/__generated__/sharedUserSettingsMutation.graphql";
import type { UserPreferencesAsyncTaskQuery } from "~/__generated__/UserPreferencesAsyncTaskQuery.graphql";
import type { CalendarSource, UserPreferencesQuery } from "~/__generated__/UserPreferencesQuery.graphql";
import type { UserPreferencesResyncCalendarsListMutation } from "~/__generated__/UserPreferencesResyncCalendarsListMutation.graphql";
import type { UserPreferencesResyncErroredCalendarsMutation } from "~/__generated__/UserPreferencesResyncErroredCalendarsMutation.graphql";
import type { UserPreferencesSetSyncedCalendarsMutation } from "~/__generated__/UserPreferencesSetSyncedCalendarsMutation.graphql";

import styles from "./UserPreferences.module.css";

const { useCallback, useContext, useEffect, useMemo, useRef, useState } = React;

const desktopSettingsAPI = window.electron?.settingsAPI;
const isMenuBarTextSupported = isMacos;

const preferencesQuery = graphql`
	query UserPreferencesQuery {
		viewer {
			user {
				id
				email
			}
			settings {
				theme
				firstWeekDay
				enableBrowserExtensionDots
				enableCalendarDescriptionLinks
				enableLiveReactions
				disableExternalUserCalendarLinks
				preferredConferenceProvider

				timezonePreferences {
					preferredTimezone {
						timezone {
							name
							description
							utcOffset
						}
						userLabel
					}
					extraTimezones {
						timezone {
							name
							description
							utcOffset
						}
						userLabel
					}
				}
			}
			integrations {
				zoom {
					hasMeetingWriteScope
				}
			}
			calendars {
				edges {
					node {
						id
						name
						source
						sourcePrimary
						syncEnabled
						removedFromApi
						color
						lastSyncDatetime
						hadErrorOnSync
					}
				}
			}
			...TimezoneSelector_viewer
			...EventFilters_viewer
		}
	}
`;

const resyncCalendarsWithErrorsMutation = graphql`
	mutation UserPreferencesResyncErroredCalendarsMutation($currentDate: String, $calendarIds: [ID!]!) {
		resyncCalendar(currentDate: $currentDate, calendarIds: $calendarIds) {
			ok
			error {
				__typename
				... on ReauthError {
					service
				}
				message
			}
			asyncResultId
		}
	}
`;

const resyncCalendarsListMutation = graphql`
	mutation UserPreferencesResyncCalendarsListMutation($source: CalendarSource!) {
		resyncCalendarsList(source: $source) {
			error {
				service
			}
			viewer {
				id
				calendars {
					edges {
						node {
							id
							name
							source
							sourcePrimary
							syncEnabled
							removedFromApi
							color
							lastSyncDatetime
							hadErrorOnSync
						}
					}
				}
			}
		}
	}
`;
const asyncTaskQuery = graphql`
	query UserPreferencesAsyncTaskQuery($taskId: JSON!) {
		viewer {
			asyncTask(taskId: $taskId) {
				taskId
				state
				info
			}
		}
	}
`;

const setSyncedCalendarsMutation = graphql`
	mutation UserPreferencesSetSyncedCalendarsMutation($syncFlags: [CalendarSyncStatus!]!) {
		setCalendarsSyncStatus(syncFlags: $syncFlags) {
			ok
			calendars {
				id
				syncEnabled
			}
		}
	}
`;

const firstDayOptions = [
	{
		label: "Saturday",
		value: "Sat",
	},
	{
		label: "Sunday",
		value: "Sun",
	},
	{
		label: "Monday",
		value: "Mon",
	},
];

function OpenInDesktopAppSetting() {
	const [enabled, setEnabled] = openInDesktopStorage.useState(currentUserStorageKey, false);

	return (
		<Row>
			<Left>
				<Label>Open in desktop app</Label>
				<SubLabel>Automatically open links in desktop app when possible</SubLabel>
			</Left>
			<Right>
				<Switch size="small" checked={enabled} onCheckedChange={setEnabled} />
			</Right>
		</Row>
	);
}

const sameDayFormat = new Intl.DateTimeFormat(undefined, { timeStyle: "short", dateStyle: undefined });
const differentDayFormat = new Intl.DateTimeFormat(undefined, { timeStyle: "short", dateStyle: "medium" });

function CalendarCheckbox(props: {
	resyncInLoad: boolean;
	resyncAsyncTaskId?: string;
	onSyncErrorClicked: () => void;
	onCheckedChange: (id: string, value: boolean) => void;
	calendar: {
		id: string;
		name: string | null;
		removedFromApi: boolean | null;
		source: string;
		sourcePrimary: boolean;
		color?: string | null;
		lastSyncDatetime: string | null;
		hadErrorOnSync: boolean | null;
		syncEnabled: boolean;
	};
	checked: boolean;
}) {
	const { calendar, checked, onCheckedChange, resyncInLoad, resyncAsyncTaskId, onSyncErrorClicked } = props;
	const changeCalendarChecked = useCallback(
		(checked: boolean) => {
			onCheckedChange(calendar.id, checked);
		},
		[calendar.id, onCheckedChange],
	);

	const lastSyncDate = useMemo(
		() => calendar.lastSyncDatetime && new Date(calendar.lastSyncDatetime),
		[calendar.lastSyncDatetime],
	);
	const isCurrentDay = lastSyncDate && isSameDay(new Date(), lastSyncDate);
	const lastSyncFormatted = useMemo(
		() => lastSyncDate && (isCurrentDay ? sameDayFormat : differentDayFormat).format(lastSyncDate),
		[lastSyncDate, isCurrentDay],
	);

	return (
		<div className={styles.calendarCheckbox}>
			<Checkbox
				id={`checkbox-${calendar.id}`}
				disabled={calendar.sourcePrimary}
				checked={calendar.sourcePrimary || !!checked}
				onCheckedChange={!calendar.sourcePrimary ? changeCalendarChecked : undefined}
				color={calendar.sourcePrimary ? "var(--navItemColor)" : (calendar.color ?? undefined)}
			/>
			<label htmlFor={`checkbox-${calendar.id}`}>
				{calendar.sourcePrimary ? (
					<>
						Primary calendar&ensp;
						<Tooltip content="Cannot disable your primary calendar">
							<FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
						</Tooltip>
					</>
				) : (
					calendar.name
				)}
				<div className={styles.calendarSubtext}>
					{calendar.removedFromApi ? (
						<div className={styles.calendarError}>
							{`This calendar was removed from ${
								calendar.source === "google" ? "Google" : calendar.source === "office365" ? "Microsoft 365" : "provider"
							}, and can no longer be synced. Deselect it to remove the events from Fellow.`}
						</div>
					) : calendar.hadErrorOnSync && calendar.syncEnabled ? (
						<>
							{resyncInLoad || resyncAsyncTaskId ? (
								"Syncing…"
							) : (
								<ButtonLink className={styles.calendarError} onClick={onSyncErrorClicked}>
									This calendar failed to sync. Click here to try again.
								</ButtonLink>
							)}
						</>
					) : calendar.lastSyncDatetime ? (
						`Synced ${lastSyncFormatted}`
					) : null}
				</div>
			</label>
		</div>
	);
}

function useRefreshCalendars(source: CalendarSource): [isRefreshing: boolean, onRefresh: () => void] {
	const [isRefreshing, toggleRefreshing] = useToggle(false);
	const onRefresh = useCallback(() => {
		toggleRefreshing();
		commitMutationPromise<UserPreferencesResyncCalendarsListMutation>(environment, {
			mutation: resyncCalendarsListMutation,
			variables: { source },
		})
			.then(res => {
				const reauthError = res.resyncCalendarsList?.error;
				if (reauthError) {
					const cb = () => {
						if (reauthError.service == "google") {
							promptGoogleAuth({ refresh: true });
						}
						if (reauthError.service == "office365") {
							promptOffice365Auth({ refresh: true });
						}
					};
					toast.error(
						`Please reconnect your ${reauthError.service === "google" ? "Google" : "Microsoft 365"} account`,
						{
							title: "Sync Error",
							// eslint-disable-next-line fellow/no-inline-disable
							// eslint-disable-next-line react/jsx-no-bind
							actions: <ToastActionButton onClick={cb}>Reconnect</ToastActionButton>,
							onClick: cb,
						},
					);
				} else {
					toast.success("Your calendars list has been refreshed", { closeButton: true });
				}
			})
			.catch(() => {
				toast.error("Failed to refresh calendars list", { autoClose: 10000, title: "Error" });
			})
			.finally(() => {
				toggleRefreshing();
			});
	}, [toggleRefreshing, source]);
	return [isRefreshing, onRefresh];
}

function RefreshButton({ provider }: { provider: CalendarSource }) {
	const [isRefreshingCalendarList, refreshCalendarList] = useRefreshCalendars(provider);
	return (
		<Button variant="secondary" size="normal" onClick={refreshCalendarList} disabled={isRefreshingCalendarList}>
			Refresh list
		</Button>
	);
}

interface VideoSelectorOption {
	label: string;
	value: ConferenceProviderOptions;
	logo?: React.ReactNode;
	connected: boolean;
}

function ConferenceProviderSingleValue({ children, ...rest }: SingleValueProps<VideoSelectorOption, false>) {
	const { data } = rest;
	return (
		<components.SingleValue {...rest}>
			{data.logo}
			{children}
		</components.SingleValue>
	);
}

function ConferenceProviderOption({ children, innerProps, ...rest }: OptionProps<VideoSelectorOption, false>) {
	const { isSelected, data } = rest;
	return (
		<components.Option
			{...rest}
			innerProps={{ ...innerProps, className: cx(innerProps.className, styles.conferenceOption) }}
		>
			<div className={styles.conferenceOptionLabel}>
				{data.logo}
				{children}
			</div>
			{data.connected === false ? (
				<span className={styles.conferenceOptionConnect}>Connect</span>
			) : isSelected ? (
				<FontAwesomeIcon icon={faCheck} />
			) : null}
		</components.Option>
	);
}

function refetchUserZoomIntegration() {
	fetchQuery(
		environment,
		graphql`
			query UserPreferencesRefetchZoomIntegrationQuery {
				viewer {
					settings {
						preferredConferenceProvider
					}
					integrations {
						zoom {
							hasMeetingWriteScope
						}
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "network-only" },
	).toPromise();
}

function clearMeetingsPanelCache() {
	if (window.relayCache != null) {
		window.relayCache.forEach((_v, k) => {
			if (k.includes("MeetingsPanelQuery")) {
				window.relayCache.delete(k);
			}
		});
	}
}

function createUserTimezoneRecord(store: RecordSourceSelectorProxy, id: string, tz: TimezoneInput) {
	const userTimezoneRecord = store.create(id, "UserTimezone");
	userTimezoneRecord.setValue(tz.userLabel, `userLabel`);

	const tzRecord = store.create(`${id}:timezone`, "Timezone");
	const { name, description, utcOffset } = getTimezoneInfo(tz.timezoneName);
	tzRecord.setValue(name, "name");
	tzRecord.setValue(description, "description");
	tzRecord.setValue(utcOffset, "utcOffset");

	return userTimezoneRecord.setLinkedRecord(tzRecord, "timezone");
}

export default function UserPreferences() {
	const themeContext = useContext(ThemeContext);
	const data = useLazyLoadQuery<UserPreferencesQuery>(preferencesQuery, {}, { fetchPolicy: "network-only" });

	const { viewer } = data,
		{ settings } = viewer;

	const provider = useMemo(
		() => viewer.calendars?.edges?.find(edge => edge?.node?.source !== "internal")?.node?.source,
		[viewer.calendars?.edges],
	);

	const [theme, setTheme] = useUserSetting("theme", settings.theme ?? "AUTO", {
		updater: useCallback(
			(store, _response, newValue) => {
				store.getRoot().getLinkedRecord("viewer")?.getLinkedRecord("settings")?.setValue(newValue, "theme");
				themeContext.setTheme(newValue);
			},
			[themeContext],
		),
	});

	const [firstWeekday, setFirstWeekday] = useUserSetting("firstWeekDay", settings.firstWeekDay ?? "Sun", {
		updater: true,
	});

	const traySettings = useDesktopSetting("tray");
	const menubarTextEnabled = traySettings ? traySettings.value.menuBarText : false;
	const setMenuBarTextEnabled = useCallback(
		(newValue: boolean) => {
			if (!traySettings) throw new Error("Can't set tray setting");
			traySettings.update({ menuBarText: newValue });
		},
		[traySettings],
	);

	const [extensionDots, setExtensionDots] = useUserSetting(
		"enableBrowserExtensionDots",
		settings.enableBrowserExtensionDots ?? false,
		{ updater: true },
	);

	const [enableCalendarDescriptionLinks, setEnableCalendarDescriptionLinks] = useUserSetting(
		"enableCalendarDescriptionLinks",
		settings.enableCalendarDescriptionLinks ?? false,
		{
			updater: true,
		},
	);

	const [disableExternalUserCalendarLinks, setDisableExternalUserCalendarLinks] = useUserSetting(
		"disableExternalUserCalendarLinks",
		settings.disableExternalUserCalendarLinks ?? false,
		{
			updater: true,
		},
	);

	const [enableLiveReactions, setEnableLiveReactions] = useUserSetting(
		"enableLiveReactions",
		settings.enableLiveReactions ?? false,
		{
			updater: true,
		},
	);

	const initialTimezonePreferences = useMemo(() => {
		const preferredTimezone = {
			timezoneName: settings.timezonePreferences?.preferredTimezone?.timezone?.name || "",
			userLabel: settings.timezonePreferences?.preferredTimezone?.userLabel || "",
		};

		let extraTimezones: TimezoneInput[] = [];

		settings.timezonePreferences?.extraTimezones?.forEach(tz => {
			if (tz === null) return;
			extraTimezones.push({
				timezoneName: tz.timezone?.name || "",
				userLabel: tz.userLabel || "",
			});
		});

		return { preferredTimezone, extraTimezones };
	}, [settings.timezonePreferences]);

	const [timezonePreferences, setTimezonePreferences] = useUserSetting(
		"timezonePreferences",
		initialTimezonePreferences,
		{
			debounce: true,
			updater: (store, data, value) => {
				const settingsNode = store.getRoot().getLinkedRecord("viewer")?.getLinkedRecord("settings");
				if (!settingsNode) return;

				const updateId = v4();

				const record = store.create(`client:newTimezonePreferences:${updateId}`, "TimezonePreferences");

				if (value?.preferredTimezone) {
					const userTimezoneRecord = createUserTimezoneRecord(
						store,
						`client:newTimezone:${updateId}-preferred`,
						value.preferredTimezone,
					);

					record.setLinkedRecord(userTimezoneRecord, "preferredTimezone");

					store
						.getRoot()
						.getLinkedRecord("viewer")
						?.getLinkedRecord("user")
						?.getLinkedRecord("timezones")
						?.setLinkedRecord(userTimezoneRecord, "preferredTimezone");
				}

				const extraTimezoneRecords = value?.extraTimezones?.reduce((records, tz, i) => {
					if (!tz) return records;
					const userTimezoneRecord = createUserTimezoneRecord(store, `client:newTimezone:${updateId}-${i}`, tz);
					return [...records, userTimezoneRecord];
				}, new Array<RecordProxy<{}>>());

				record.setLinkedRecords(extraTimezoneRecords, "extraTimezones");

				settingsNode.setLinkedRecord(record, "timezonePreferences");
			},
		},
	);

	const timezoneSetters = useMemo(
		() => ({
			preferred: (value: string) => {
				if (!timezonePreferences) return;

				let preferredTimezone = timezonePreferences.preferredTimezone;

				if (!preferredTimezone) {
					preferredTimezone = {
						timezoneName: value,
						userLabel: "",
					};
				} else {
					if (preferredTimezone.timezoneName === value) return;

					preferredTimezone = {
						timezoneName: value,
						userLabel: preferredTimezone.userLabel,
					};
				}

				setTimezonePreferences({
					...timezonePreferences,
					preferredTimezone,
				});
			},
			second: (value: string) => {
				if (!timezonePreferences || !timezonePreferences.extraTimezones?.[0]) return;

				let extraTimezones = [
					{
						timezoneName: value,
						userLabel: timezonePreferences.extraTimezones[0].userLabel || "",
					},
				];

				if (timezonePreferences.extraTimezones[1]) extraTimezones.push(timezonePreferences.extraTimezones[1]);

				setTimezonePreferences({
					...timezonePreferences,
					extraTimezones,
				});
			},
			third: (value: string) => {
				if (!timezonePreferences || !timezonePreferences.extraTimezones?.[1]) return;

				let extraTimezones = [
					timezonePreferences.extraTimezones[0],
					{
						timezoneName: value,
						userLabel: timezonePreferences.extraTimezones[1]?.userLabel || "",
					},
				];

				setTimezonePreferences({
					...timezonePreferences,
					extraTimezones,
				});
			},
		}),
		[timezonePreferences, setTimezonePreferences],
	);

	const userLabelSetters = useMemo(
		() => ({
			preferred: (value: string) => {
				if (!timezonePreferences || !timezonePreferences.preferredTimezone) return;
				if (timezonePreferences.preferredTimezone.userLabel === value) return;

				setTimezonePreferences({
					...timezonePreferences,
					preferredTimezone: {
						...timezonePreferences.preferredTimezone,
						userLabel: value,
					},
				});
			},
			second: (value: string) => {
				if (!timezonePreferences || !timezonePreferences.extraTimezones || !timezonePreferences.extraTimezones[0])
					return;
				if (timezonePreferences.extraTimezones[0].userLabel === value) return;

				setTimezonePreferences({
					...timezonePreferences,
					extraTimezones: [
						{
							...timezonePreferences.extraTimezones[0],
							userLabel: value,
						},
						timezonePreferences.extraTimezones[1],
					],
				});
			},
			third: (value: string) => {
				if (!timezonePreferences || !timezonePreferences.extraTimezones || !timezonePreferences.extraTimezones[1])
					return;
				if (timezonePreferences.extraTimezones[1].userLabel === value) return;

				setTimezonePreferences({
					...timezonePreferences,
					extraTimezones: [
						timezonePreferences.extraTimezones[0],
						{
							...timezonePreferences.extraTimezones[1],
							userLabel: value,
						},
					],
				});
			},
		}),
		[timezonePreferences, setTimezonePreferences],
	);

	const [timezoneUserLabelValues, setTimezoneUserLabelValues] = useState({
		preferred: timezonePreferences?.preferredTimezone?.userLabel ?? "",
		second: timezonePreferences?.extraTimezones?.[0]?.userLabel ?? "",
		third: timezonePreferences?.extraTimezones?.[1]?.userLabel ?? "",
	});

	const [timezoneLabelErrors, setTimezoneLabelErrors] = useState({
		preferred: false,
		second: false,
		third: false,
	});

	const onChangeTimezoneUserLabelValue = useCallback(
		(timezone: "preferred" | "second" | "third") => (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value.toUpperCase();

			setTimezoneUserLabelValues(prev => ({ ...prev, [timezone]: value }));

			const errors = !value.match(/^[\w-]*$/);
			setTimezoneLabelErrors(prev => ({ ...prev, [timezone]: errors }));

			if (!errors) userLabelSetters[timezone](value);
		},
		[userLabelSetters],
	);

	const addTimezone = useCallback(() => {
		let extraTimezones = timezonePreferences?.extraTimezones ?? [];

		//MEET-2039: There's an `undefined` added somewhere, filtering it out here fixes the issue
		extraTimezones = extraTimezones.filter(tz => Boolean(tz));

		extraTimezones = [...extraTimezones, { timezoneName: "", userLabel: "" }];
		setTimezonePreferences({ ...timezonePreferences, extraTimezones });
	}, [timezonePreferences, setTimezonePreferences]);

	const removeExtraTimezone = useCallback(
		(timezone: "second" | "third") => () => {
			setTimezoneUserLabelValues(prev => {
				const newValues = {
					...prev,
					second: timezone === "second" ? (prev.third ? prev.third : "") : prev.second,
					third: "",
				};

				return newValues;
			});

			let extraTimezones = timezonePreferences?.extraTimezones ?? [];
			if (timezone === "second") {
				if (extraTimezones[1]) extraTimezones = [extraTimezones[1]];
				else extraTimezones = [];
			} else extraTimezones = [extraTimezones[0]];

			setTimezonePreferences({ ...timezonePreferences, extraTimezones });
		},
		[timezonePreferences, setTimezonePreferences],
	);

	const updateCalendarsSyncStatus = useCallback(async (calendarId: string, syncEnabled: boolean) => {
		const result = await commitMutationPromise<UserPreferencesSetSyncedCalendarsMutation>(environment, {
			mutation: setSyncedCalendarsMutation,
			variables: { syncFlags: [{ calendarId, syncEnabled }] },
		});
		if (!result.setCalendarsSyncStatus?.ok) {
			toast.error("An error occurred while changing calendar status. Please contact support");
		} else {
			clearMeetingsPanelCache();
		}
	}, []);

	const [isRequestingResync, setIsRequestingResync] = useState(false);
	const [pendingResyncId, setPendingResyncId] = useState<string | null>(null);
	const resyncCalendarsWithErrors = useCallback(() => {
		if (isRequestingResync || pendingResyncId) return;

		const erroredCalendarIds = compact(
			viewer.calendars?.edges.filter(c => c?.node?.hadErrorOnSync).map(c => c?.node?.id),
		);
		if (erroredCalendarIds.length === 0) return;

		setIsRequestingResync(true);

		commitMutation<UserPreferencesResyncErroredCalendarsMutation>(environment, {
			mutation: resyncCalendarsWithErrorsMutation,
			variables: {
				currentDate: new Date().toISOString(),
				calendarIds: erroredCalendarIds,
			},
			onCompleted: ({ resyncCalendar }: UserPreferencesResyncErroredCalendarsMutation["response"]) => {
				setIsRequestingResync(false);

				if (!resyncCalendar) return;

				const { ok, asyncResultId } = resyncCalendar;
				if (ok && asyncResultId) {
					setPendingResyncId(asyncResultId[0]);
				}
			},
		});
	}, [isRequestingResync, pendingResyncId, viewer.calendars?.edges]);

	const pendingResyncCheckCount = useRef(0);
	const pendingResyncCheckTimeout = useRef<number | null>(null);
	useEffect(() => {
		async function checkPendingResync() {
			if (!pendingResyncId) return;

			const response = await fetchQuery<UserPreferencesAsyncTaskQuery>(environment, asyncTaskQuery, {
				taskId: pendingResyncId,
			}).toPromise();
			if (!response) return;

			const { asyncTask } = response.viewer;
			if (!asyncTask) return;

			const { state, info } = asyncTask;

			if (state === "PENDING") {
				pendingResyncCheckCount.current = pendingResyncCheckCount.current + 1;
				pendingResyncCheckTimeout.current = window.setTimeout(
					checkPendingResync,
					Math.min(2 ** pendingResyncCheckCount.current * 1000, 60 * 1000),
				);
			} else {
				setPendingResyncId(null);
				setIsRequestingResync(false); // TODO: delete?
				pendingResyncCheckCount.current = 0;
				if (pendingResyncCheckTimeout.current) window.clearTimeout(pendingResyncCheckTimeout.current);
				pendingResyncCheckTimeout.current = null;

				if (state === "SUCCESS") {
					toast.success("Calendars Synced");
					// retry(); // TODO: trigger refetch here I guess
				} else {
					const { type } = info.error;
					if (type == "NotAcquired") {
						toast.error("Try again in a little bit", { title: "A sync is already in progress" });
					} else {
						toast.error("There was an issue syncing your calendar(s)");
					}
				}
			}
		}
		checkPendingResync();
	}, [pendingResyncId]);

	const zoomConferenceFeatureEnabled = useFeature("zoom_conference_provider", "account") ?? false;
	const zoomConferenceSwitchEnabled = useSwitch("zoom_conference_provider") ?? false;

	const zoomIsConnected = !!viewer.integrations.zoom?.hasMeetingWriteScope;
	const videoProviderOptions = useMemo(() => {
		let conferencePreferenceOptions: VideoSelectorOption[] = [];

		const hasGoogleCalendar = viewer.calendars?.edges.some(c => c?.node?.source == "google");
		const hasOfficeCalendar = viewer.calendars?.edges.some(c => c?.node?.source == "office365");

		if (hasGoogleCalendar) {
			conferencePreferenceOptions.push({
				label: "Google",
				value: "GOOGLE",
				connected: true,
				logo: <GoogleMeetLogo style={{ height: "1em", width: "1em", marginRight: "0.5em", verticalAlign: "-0.1em" }} />,
			});
		}

		if (hasOfficeCalendar) {
			conferencePreferenceOptions.push({
				label: "Teams",
				value: "TEAMS",
				connected: true,
				logo: <MSTeamsLogo style={{ height: "1em", width: "1em", marginRight: "0.5em", verticalAlign: "-0.1em" }} />,
			});
		}

		conferencePreferenceOptions.push(
			{
				label: "Zoom",
				value: "ZOOM",
				connected: zoomIsConnected,
				logo: <ZoomLogo style={{ height: "1em", width: "1em", marginRight: "0.5em", verticalAlign: "-0.1em" }} />,
			},
			{
				label: "No preference",
				value: "NONE",
				connected: true,
			},
		);
		return conferencePreferenceOptions;
	}, [viewer.calendars?.edges, zoomIsConnected]);

	const [preferredConferenceProvider, setPreferredConferenceProvider] = useUserSetting(
		"preferredConferenceProvider",
		settings.preferredConferenceProvider ?? "NONE",
		{
			updater: true,
		},
	);
	const onConferenceProviderChange = useCallback(
		(newValue: SingleValue<VideoSelectorOption>) => {
			const value = newValue?.value ?? "NONE";

			if (value === "ZOOM" && !zoomIsConnected) {
				window.open("/zoom/oauth/", "_blank", "noopener noreferrer");
			} else {
				setPreferredConferenceProvider(value);
			}
		},
		[setPreferredConferenceProvider, zoomIsConnected],
	);

	useEffect(() => {
		let userId = viewer.user.id;
		userId = userId.replace(/=+$/, ""); // Note: this is half cargo-cult, I'm not really sure why this is needed
		const socket = WebsocketClient.getInstance().getOrJoinGroup(`integrations.${userId}`, { userId });

		async function onSocketMessage(message: { type: string }) {
			if (message.type == "ZOOM_CONNECTED") {
				refetchUserZoomIntegration();
			}
		}

		socket.on("message", onSocketMessage);

		return () => {
			socket.off("message", onSocketMessage);
			socket.close();
		};
	}, [viewer.user.id]);

	const addTimezoneButton = (
		<Button variant="tertiary" size="normal" className={styles.addTimezoneButton} onClick={addTimezone}>
			<FontAwesomeIcon icon={faPlus} />
			&ensp;Add timezone
		</Button>
	);

	const userLabelErrorMessage = (
		<small className={styles.timezoneCustomLabelError}>Only letters, numbers, and dashes are allowed.</small>
	);

	return (
		<div className={styles.main}>
			<SettingsPageTitle>Preferences</SettingsPageTitle>
			<SettingsPageSubtitle>Manage your preferences</SettingsPageSubtitle>
			<Divider />
			<SectionTitle>Appearance</SectionTitle>
			<Row>
				<Left>
					<Label>Interface theme</Label>
					<SubLabel>Select a theme or sync to your system preferences</SubLabel>
				</Left>
				<Right>
					<ThemeSelector value={theme ?? "AUTO"} onChange={setTheme} />
				</Right>
			</Row>
			{growthbook.isOn(LIVE_REACTIONS_FEATURE_FLAG_ID) && (
				<>
					<Divider />
					<Row>
						<Left>
							<Label>Live reactions</Label>
							<SubLabel>Enable animated live reactions during meetings.</SubLabel>
						</Left>
						<Right>
							<Switch size="small" checked={enableLiveReactions ?? false} onCheckedChange={setEnableLiveReactions} />
						</Right>
					</Row>
				</>
			)}
			<Divider />
			<SectionTitle>Display</SectionTitle>
			<Row>
				<Left>
					<Label>First day of the week</Label>
					<SubLabel>Customize the start of week you would like to use in Fellow</SubLabel>
				</Left>
				<Right>
					<Selector
						condensed
						onChange={useCallback(({ value }) => setFirstWeekday(value), [setFirstWeekday])}
						options={firstDayOptions}
						value={firstDayOptions.find(o => o.value === firstWeekday)}
					/>
				</Right>
			</Row>
			<Divider />
			<Row>
				<Label>Timezones</Label>
			</Row>
			<Row>
				<Left>
					<SubLabel>
						Primary timezone&ensp;
						<Tooltip
							maxWidth={140}
							content="This timezone will be used as the default when displaying and creating events."
							className={styles.centeredTooltip}
						>
							<FontAwesomeIcon icon={faInfoCircle} />
						</Tooltip>
					</SubLabel>
					<div className={styles.timezoneInputsContainer}>
						<TimezoneSelector
							value={timezonePreferences?.preferredTimezone?.timezoneName ?? ""}
							onChange={timezoneSetters["preferred"]}
							viewer={viewer}
						/>
						&ensp;
						<Input
							className={cx(styles.timezoneSelectorCustomLabelInput, {
								[styles.error]: timezoneLabelErrors["preferred"],
							})}
							placeholder="Label"
							maxLength={4}
							value={timezoneUserLabelValues.preferred}
							onChange={onChangeTimezoneUserLabelValue("preferred")}
							name="timezone_label_preferred"
						/>
					</div>
					{timezoneLabelErrors["preferred"] && userLabelErrorMessage}
					{(!timezonePreferences?.extraTimezones || timezonePreferences.extraTimezones.length === 0) &&
						addTimezoneButton}
				</Left>
			</Row>
			{timezonePreferences?.extraTimezones?.[0] && (
				<Row>
					<Left>
						<SubLabel>Add up to 2 more timezones to be displayed on the calendar.</SubLabel>
						<div className={styles.timezoneInputsContainer}>
							<TimezoneSelector
								value={timezonePreferences.extraTimezones[0].timezoneName ?? ""}
								onChange={timezoneSetters["second"]}
								viewer={viewer}
							/>
							&ensp;
							<Input
								className={cx(styles.timezoneSelectorCustomLabelInput, {
									[styles.error]: timezoneLabelErrors["second"],
								})}
								placeholder="Label"
								maxLength={4}
								value={timezoneUserLabelValues.second}
								onChange={onChangeTimezoneUserLabelValue("second")}
								name="timezone_label_second"
							/>
							&ensp;
							<IconButton icon={faTrashCan} onClick={removeExtraTimezone("second")} />
						</div>
						{timezoneLabelErrors["second"] && userLabelErrorMessage}
						{!timezonePreferences?.extraTimezones?.[1] ? (
							addTimezoneButton
						) : (
							<>
								<div className={styles.timezoneInputsContainer}>
									<TimezoneSelector
										value={timezonePreferences.extraTimezones[1].timezoneName ?? ""}
										onChange={timezoneSetters["third"]}
										viewer={viewer}
									/>
									&ensp;
									<Input
										className={cx(styles.timezoneSelectorCustomLabelInput, {
											[styles.error]: timezoneLabelErrors["third"],
										})}
										placeholder="Label"
										maxLength={4}
										value={timezoneUserLabelValues.third}
										onChange={onChangeTimezoneUserLabelValue("third")}
										name="timezone_label_third"
									/>
									&ensp;
									<IconButton icon={faTrashCan} onClick={removeExtraTimezone("third")} />
								</div>
								{timezoneLabelErrors["third"] && userLabelErrorMessage}
							</>
						)}
					</Left>
				</Row>
			)}
			{desktopSettingsAPI && isMenuBarTextSupported && (
				<>
					<Divider />
					<Row>
						<Left>
							<Label>Tray menu</Label>
							<SubLabel>Show upcoming event in menubar</SubLabel>
						</Left>
						<Right>
							<Switch size="small" checked={menubarTextEnabled} onCheckedChange={setMenuBarTextEnabled} />
						</Right>
					</Row>
				</>
			)}
			<Divider />
			<Row>
				<Left>
					<Label>Browser extension event dots</Label>
					<SubLabel>Show dots on events and dates in Google Calendar to indicate agendas and action items due</SubLabel>
				</Left>
				<Right>
					<Switch size="small" checked={extensionDots ?? false} onCheckedChange={setExtensionDots} />
				</Right>
			</Row>
			<Divider />
			<SectionTitle>Calendar Settings</SectionTitle>
			<Row>
				<Left>
					<Label>Synced Calendars</Label>
					<SubLabel>Fellow can sync additional calendars from your calendar provider.</SubLabel>
				</Left>
				<Right>{provider && <RefreshButton provider={provider} />}</Right>
			</Row>
			{provider && (
				<div className={styles.calendarsAccountInfo}>
					{provider === "google" ? (
						<GoogleLogo className={styles.calendarsAccountLogo} />
					) : provider === "office365" ? (
						<OutlookLogo className={styles.calendarsAccountLogo} />
					) : null}
					&ensp;
					<span>{viewer.user.email}</span>
				</div>
			)}
			{viewer.calendars?.edges
				?.filter(edge => edge?.node?.source !== "internal")
				.map(
					edge =>
						edge?.node && (
							<CalendarCheckbox
								resyncInLoad={false}
								onCheckedChange={updateCalendarsSyncStatus}
								onSyncErrorClicked={resyncCalendarsWithErrors}
								checked={edge.node.syncEnabled}
								key={edge.node.id}
								calendar={edge.node}
							/>
						),
				)}
			<p style={{ font: "var(--paragraph-5)" }}>
				To create or delete calendars, visit your {/* TODO: make this a link, or find out that we can't */}
				{provider === "google"
					? "Google calendar"
					: provider === "office365"
						? "Microsoft 365 calendar"
						: "calendar"}{" "}
				directly. Fellow is only able to sync calendars to which you have full write access.
			</p>
			<InfoBanner>
				Disabled calendars are not displayed in your meetings panel, but associated notes can still be accessed in
				Search.
			</InfoBanner>
			<Divider />
			<EventFiltersSettings viewer={viewer} />
			<Divider />
			<SectionTitle>Behavior</SectionTitle>
			<Row>
				<Row.Left>
					<Label>Quick access links</Label>
					<SubLabel>
						Quick links are added to the event description and allow attendees to quickly access the associated Fellow
						note.
					</SubLabel>
				</Row.Left>
				<Row.Right>
					<Switch
						size="small"
						checked={enableCalendarDescriptionLinks ?? false}
						onCheckedChange={setEnableCalendarDescriptionLinks}
					/>
				</Row.Right>
			</Row>
			<div style={{ margin: "1rem 0", font: "var(--paragraph-5)" }}>
				<RadioInput
					name="quick_access_links_external"
					value={disableExternalUserCalendarLinks ? "internal_only" : "internal_and_external"}
					options={[
						{ value: "internal_and_external", label: "Add for events with internal and external attendees" },
						{ value: "internal_only", label: "Add for events with only internal attendees" },
					]}
					onChange={useCallback(
						(value: string) => setDisableExternalUserCalendarLinks(value === "internal_only"),
						[setDisableExternalUserCalendarLinks],
					)}
					disabled={!enableCalendarDescriptionLinks}
				/>
			</div>
			{(zoomConferenceFeatureEnabled || zoomConferenceSwitchEnabled) && (
				<>
					<Divider />
					<Row>
						<Left>
							<Label>Video conferencing provider</Label>
							<SubLabel>Choose a default video conferencing provider for when you create events</SubLabel>
						</Left>
						<Right>
							<Selector
								condensed
								menuPlacement="auto"
								options={videoProviderOptions}
								value={videoProviderOptions.find(({ value = "NONE" }) => value === preferredConferenceProvider)}
								onChange={onConferenceProviderChange}
								components={{ Option: ConferenceProviderOption, SingleValue: ConferenceProviderSingleValue }}
								styles={{
									menu: (provided, props) => ({
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										...(condensedStyles.menu ? condensedStyles.menu(provided, props as any) : provided),
										right: 0,
									}),
								}}
							/>
						</Right>
					</Row>
				</>
			)}
			{inApplicableEnvironment && (
				<>
					<Divider />
					<OpenInDesktopAppSetting />
				</>
			)}
			{desktopSettingsAPI && (
				<>
					<Divider />
					<DesktopAppSettings />
				</>
			)}
		</div>
	);
}
